<template>
  <div class="space-y-6">
    <!-- Loading State -->
    <div
      v-if="loading"
      class="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-12 text-center"
    >
      <div
        class="flex items-center justify-center gap-3 text-gray-600 dark:text-gray-300"
      >
        <span
          class="inline-flex h-5 w-5 animate-spin rounded-full border-2 border-blue-500 border-r-transparent"
        ></span>
        Loading subscription details...
      </div>
    </div>

    <!-- Error State -->
    <div
      v-else-if="error"
      class="rounded-lg border border-red-200 bg-red-50 p-6 dark:border-red-800 dark:bg-red-900/30"
    >
      <div class="flex items-center justify-between">
        <p class="text-sm text-red-700 dark:text-red-200">{{ error }}</p>
        <button
          type="button"
          class="text-xs font-medium text-red-700 hover:text-red-800 dark:text-red-200"
          @click="loadSubscription"
        >
          Retry
        </button>
      </div>
    </div>

    <template v-else>
      <!-- Subscription Status Card -->
      <div class="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6">
        <div class="md:flex items-center justify-between mb-4">
          <div>
            <h3 class="text-lg font-semibold text-gray-900 dark:text-white">
              Subscription Status
            </h3>
            <p class="text-sm text-gray-500 dark:text-gray-400">
              Your current plan and billing information
            </p>
          </div>
          <span
            class="inline-flex items-center rounded-full px-3 py-1 text-xs font-semibold uppercase tracking-wide md:my-0 my-3"
            :class="getStatusBadgeClass(subscription.status)"
          >
            {{ getStatusLabel(subscription.status) }}
          </span>
        </div>

        <!-- Automatic Grace Period Alert -->
        <div
          v-if="subscription.in_automatic_grace"
          class="mb-4 rounded-lg border border-green-200 bg-green-50 p-4 dark:border-green-900 dark:bg-green-900/20"
        >
          <div class="flex items-start gap-3">
            <i
              class="fas fa-gift text-green-600 dark:text-green-400 mt-0.5"
            ></i>
            <div class="flex-1">
              <p
                class="text-sm font-semibold text-green-900 dark:text-green-100"
              >
                Usage Grace Period
              </p>
              <p class="mt-1 text-sm text-green-800 dark:text-green-200">
                {{ subscription.grace_reason }}
              </p>
              <p
                v-if="subscription.in_usage_grace"
                class="mt-1 text-xs text-green-700 dark:text-green-300"
              >
                Sales in last 30 days:
                {{ subscription.sale_cart_count || 0 }}
              </p>
            </div>
          </div>
        </div>

        <!-- Subscription Grace Period Alert -->
        <div
          v-if="
            subscription.in_grace_period && !subscription.in_automatic_grace
          "
          class="mb-4 rounded-lg border border-yellow-200 bg-yellow-50 p-4 dark:border-yellow-900 dark:bg-yellow-900/20"
        >
          <div class="flex items-start gap-3">
            <i
              class="fas fa-exclamation-triangle text-yellow-600 dark:text-yellow-400 mt-0.5"
            ></i>
            <div class="flex-1">
              <p
                class="text-sm font-semibold text-yellow-900 dark:text-yellow-100"
              >
                ⏰ Grace Period Active
              </p>
              <p class="mt-1 text-sm text-yellow-800 dark:text-yellow-200">
                Your subscription expired on
                {{ formatDate(subscription.expires_at) }}. Grace period ends
                {{ formatDate(subscription.grace_period_ends_at) }}.
              </p>
              <p class="mt-1 text-xs text-yellow-700 dark:text-yellow-300">
                Renew soon to avoid service interruption.
              </p>
            </div>
          </div>
        </div>

        <!-- Plan Details -->
        <div v-if="subscription.plan" class="grid gap-4 md:grid-cols-2">
          <div
            class="rounded-lg border border-gray-200 bg-gray-50 p-4 dark:border-gray-700 dark:bg-gray-900/40"
          >
            <p
              class="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400"
            >
              Current Plan
            </p>
            <p class="mt-1 text-lg font-semibold text-gray-900 dark:text-white">
              {{ subscription.plan.display_name || subscription.plan.name }}
            </p>
            <p
              v-if="subscription.plan.description"
              class="mt-1 text-xs text-gray-600 dark:text-gray-400"
            >
              {{ subscription.plan.description }}
            </p>
          </div>

          <div
            class="rounded-lg border border-gray-200 bg-gray-50 p-4 dark:border-gray-700 dark:bg-gray-900/40"
          >
            <p
              class="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400"
            >
              Plan Price
            </p>
            <p class="mt-1 text-lg font-semibold text-gray-900 dark:text-white">
              {{
                formatPrice(
                  subscription.plan.price_amount,
                  subscription.plan.currency
                )
              }}
            </p>
            <p class="mt-1 text-xs text-gray-600 dark:text-gray-400">
              Monthly
            </p>
          </div>

          <div
            v-if="subscription.subscription"
            class="rounded-lg border border-gray-200 bg-gray-50 p-4 dark:border-gray-700 dark:bg-gray-900/40"
          >
            <p
              class="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400"
            >
              Start Date
            </p>
            <p class="mt-1 text-sm text-gray-900 dark:text-white">
              {{ formatDate(subscription.subscription.starts_at) }}
            </p>
          </div>

          <div
            v-if="subscription.subscription"
            class="rounded-lg border border-gray-200 bg-gray-50 p-4 dark:border-gray-700 dark:bg-gray-900/40"
          >
            <p
              class="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400"
            >
              {{
                subscription.in_grace_period
                  ? "Grace Period Ends"
                  : "Expires On"
              }}
            </p>
            <p class="mt-1 text-sm text-gray-900 dark:text-white">
              {{
                formatDate(
                  subscription.in_grace_period
                    ? subscription.grace_period_ends_at
                    : subscription.expires_at
                )
              }}
            </p>
          </div>
        </div>

        <!-- No Active Plan -->
        <div v-else class="text-center py-8">
          <i class="fas fa-file-invoice-dollar text-gray-400 text-4xl mb-4"></i>
          <p class="text-gray-600 dark:text-gray-400 mb-2">
            No active subscription
          </p>
          <p class="text-sm text-gray-500 dark:text-gray-500">
            Contact support to activate a subscription plan
          </p>
        </div>
      </div>

      <!-- Features Card -->
      <div
        v-if="subscription.plan && subscription.plan.features"
        class="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6"
      >
        <h3 class="text-lg font-semibold text-gray-900 dark:text-white mb-4">
          Plan Features
        </h3>
        <div class="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          <div
            v-for="(value, key) in subscription.plan.features"
            :key="key"
            class="flex items-center gap-2 rounded-lg border border-gray-200 bg-gray-50 p-3 dark:border-gray-700 dark:bg-gray-900/40"
          >
            <i class="fas fa-check-circle text-green-500"></i>
            <div>
              <p class="text-sm font-medium text-gray-900 dark:text-white">
                {{ formatFeatureName(key) }}
              </p>
              <p class="text-xs text-gray-600 dark:text-gray-400">
                {{ formatFeatureValue(value) }}
              </p>
            </div>
          </div>
        </div>
      </div>

      <!-- Payment Info (if available) -->
      <div
        v-if="subscription.subscription?.payment_reference"
        class="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6"
      >
        <h3 class="text-lg font-semibold text-gray-900 dark:text-white mb-4">
          Payment Information
        </h3>
        <div class="grid gap-4 md:grid-cols-2">
          <div>
            <p
              class="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400"
            >
              Payment Reference
            </p>
            <p class="mt-1 text-sm font-mono text-gray-900 dark:text-white">
              {{ subscription.subscription.payment_reference }}
            </p>
          </div>
          <div v-if="subscription.subscription.paid_at">
            <p
              class="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400"
            >
              Paid On
            </p>
            <p class="mt-1 text-sm text-gray-900 dark:text-white">
              {{ formatDate(subscription.subscription.paid_at) }}
            </p>
          </div>
        </div>
      </div>
    </template>
  </div>
</template>

<script>
import { ref, onMounted } from "vue";
import http from "@/helper/http";

export default {
  name: "StoreSubscriptionCard",
  props: {
    storeId: {
      type: Number,
      required: true,
    },
  },
  setup(props) {
    const loading = ref(false);
    const error = ref("");
    const subscription = ref({
      has_active_subscription: false,
      plan: null,
      subscription: null,
      in_grace_period: false,
      in_automatic_grace: false,
      status: "NO_SUBSCRIPTION",
    });

    const loadSubscription = async () => {
      loading.value = true;
      error.value = "";
      try {
        const response = await http.get(
          `/v1/stores/${props.storeId}/subscription`
        );
        subscription.value = response.data || {};
      } catch (err) {
        console.error("Failed to load subscription", err);
        error.value =
          err?.response?.data?.error?.message ||
          "Unable to load subscription details.";
      } finally {
        loading.value = false;
      }
    };

    const getStatusBadgeClass = (status) => {
      const classes = {
        ACTIVE:
          "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300",
        EXPIRED: "bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300",
        CANCELLED:
          "bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-300",
        SUSPENDED:
          "bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-300",
        AUTOMATIC_GRACE:
          "bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300",
        NO_SUBSCRIPTION:
          "bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-300",
      };
      return classes[status] || classes.NO_SUBSCRIPTION;
    };

    const getStatusLabel = (status) => {
      const labels = {
        ACTIVE: "Active",
        EXPIRED: "Expired",
        CANCELLED: "Cancelled",
        SUSPENDED: "Suspended",
        AUTOMATIC_GRACE: "Grace Period",
        NO_SUBSCRIPTION: "No Subscription",
      };
      return labels[status] || status;
    };

    const formatPrice = (amount, currency) => {
      if (!amount) return "Free";
      const formatter = new Intl.NumberFormat("en-US", {
        style: "currency",
        currency: currency || "RWF",
        minimumFractionDigits: 0,
      });
      return formatter.format(amount);
    };

    const formatDate = (dateString) => {
      if (!dateString) return "N/A";
      return new Date(dateString).toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric",
      });
    };

    const formatFeatureName = (key) => {
      return key.replace(/_/g, " ").replace(/\b\w/g, (l) => l.toUpperCase());
    };

    const formatFeatureValue = (value) => {
      if (typeof value === "boolean") {
        return value ? "Enabled" : "Disabled";
      }
      if (typeof value === "number") {
        return value.toLocaleString();
      }
      return value;
    };

    onMounted(() => {
      loadSubscription();
    });

    return {
      loading,
      error,
      subscription,
      loadSubscription,
      getStatusBadgeClass,
      getStatusLabel,
      formatPrice,
      formatDate,
      formatFeatureName,
      formatFeatureValue,
    };
  },
};
</script>
